/**
 * The column filter MODEL — operators typed by what the column is (#959).
 *
 * ── the three things this file refuses to do ──────────────────────────────
 * 1. **It does not fetch anything.** `<DataGrid>`'s header states the invariant
 *    that made it safe to put a grid in the control kit: no query hook, no saved
 *    view, no persona, no `@solum/sdk` import. A filter editor that resolved a
 *    column's type by calling `describe_semantics` would break that in one line,
 *    and the portal — which resolves its columns through its OWN persona-scoped
 *    read — would get the console's answer. So the host resolves a
 *    `ColumnFilterField` from whatever metamodel read it already does, and hands
 *    it in.
 * 2. **It does not own a vocabulary.** A status column's operators are a
 *    multi-select over the STATUS MACHINE's states, and the machine is data. The
 *    options arrive on the field; this file never names one.
 * 3. **It does not offer an operator the server cannot run.** See below — this
 *    is the constraint the whole feature hangs off.
 *
 * ── every operator here is SERVER-SIDE, and that is not a preference ───────
 * `apps/console`'s list pushes `view.filters` to PostgREST through
 * `filtersToPostgrest`, which is **all-or-nothing**: one leaf it cannot
 * translate faithfully and the ENTIRE view falls back to filtering a capped
 * page in the browser. On 204,560 invoices that path reported **125 matches
 * against a true 50,022** (#113). So an operator that does not translate is not
 * a slightly-worse operator — it is a switch that silently turns the whole list
 * into a wrong number, including the OTHER columns' filters.
 *
 * Which is why the operator sets below are exactly the ops
 * `@solum/sdk`'s `leafToPostgrest` translates, per kind, and why
 * `apps/console/src/list/columnFilter.serverside.test.ts` drives that seam
 * rather than trusting this comment: it takes every operator this file offers
 * for every kind, builds the leaf the editor would emit, and asserts the
 * translator returns a filter. Nothing here can quietly grow an operator that
 * takes the list off the server.
 *
 * ── what is NOT offered, and why it is absent rather than degraded ─────────
 * **`is empty`.** In the JS evaluator it means `v == null || v === ''`. On a
 * text column PostgREST needs a disjunction for that (`or=(f.is.null,…)`), and
 * `leafToPostgrest` refuses composites by design — so offering it would trip the
 * all-or-nothing rule above and take the list client-side. Offering it only on
 * the kinds where `''` is impossible (number, date, ref — where `is.null` alone
 * IS faithful) was the other candidate and is worse: an operator list that
 * appears and disappears per column, for a reason nothing on screen explains.
 * It is a stated follow-up with a designed translation, not an oversight.
 *
 * **`contains` / `starts with` / `ends with` off text.** `ilike` is a text
 * operator; `numeric ~~* unknown` is not a thing. `leafToPostgrest` refuses
 * them off text, so they are not offered off text.
 *
 * **`on` (equality) for `datetime`.** A timestamp column compared to a date the
 * user typed is a question with two defensible answers (the instant, or the
 * day), and the JS and SQL sides would pick differently. `date` keeps `on`,
 * where there is only one answer.
 */

/**
 * One `{field, op, value}` condition — structurally the core rules `Leaf`, and
 * the same payload `core.view.filters`, the MCP filter tools and
 * `filtersToPostgrest` all speak. Redeclared here rather than imported so this
 * package keeps its zero data dependencies; the shape is pinned by the seam
 * test in the console, which imports both.
 */
export interface ColumnFilterLeaf { field: string; op: string; value?: unknown }

/**
 * What a column IS, in the metamodel's own words — `core.field_meta.kind`,
 * plus `unknown` for a column the dictionary does not describe.
 */
export type ColumnFilterKind =
  | 'text' | 'email' | 'phone' | 'country'
  | 'number' | 'money' | 'percent'
  | 'date' | 'datetime'
  | 'boolean' | 'option' | 'ref' | 'unknown'

export interface ColumnFilterOption { value: string; label: string }

/** The column, as the HOST resolved it from the metamodel. */
export interface ColumnFilterField {
  key: string
  label: string
  kind: ColumnFilterKind
  /**
   * For `option`: the vocabulary. The host resolves it — a status column's
   * states come from `core.status_machine` / `core.status_state`, everything
   * else from `field_meta.options` (which may itself be a picklist catalog).
   * An `option` field that arrives with none falls back to the conservative
   * set, and `conservativeReason` says so on screen.
   */
  options?: ColumnFilterOption[]
  /** For `ref`: what the column points at, so the host can pick the right lookup. */
  refEntity?: string
  /**
   * The operators the HOST's read path can actually apply to this column, when
   * that is NARROWER than the kind's own set — e.g. a record type read through
   * a governed door with a closed vocabulary (#2444). The editor offers only
   * these (never more than the kind's set, whatever is passed); an empty list
   * means the column cannot be filtered here, and the editor says so instead
   * of offering an operator the list would refuse. Absent ⇒ the kind's set.
   */
  allowedOps?: readonly string[]
  /** Why `allowedOps` narrowed the set, in words for a person. Shown in the menu. */
  restrictionReason?: string
}

/** How the editor renders the VALUE for an operator. */
export type ColumnFilterInput = 'text' | 'number' | 'date' | 'datetime' | 'option' | 'options' | 'ref' | 'boolean' | 'range'

export interface ColumnFilterOperator {
  /** the leaf `op` — or `between`, the one operator that emits TWO leaves */
  op: string
  label: string
  input: ColumnFilterInput
}

/* ── the operator sets ─────────────────────────────────────────────────────
 * Each entry names the leaf op `leafToPostgrest` translates for that kind. The
 * labels are the words a user reads; the ops are the grammar. Both halves are
 * asserted — the labels by the editor's specs, the ops by the seam test. */

const TEXT_OPS: ColumnFilterOperator[] = [
  { op: 'contains', label: 'contains', input: 'text' },
  { op: 'eq', label: 'is', input: 'text' },
  { op: 'ne', label: 'is not', input: 'text' },
  { op: 'starts', label: 'starts with', input: 'text' },
  { op: 'ends', label: 'ends with', input: 'text' },
]

const NUMBER_OPS: ColumnFilterOperator[] = [
  { op: 'eq', label: '=', input: 'number' },
  { op: 'ne', label: '≠', input: 'number' },
  { op: 'gt', label: '>', input: 'number' },
  { op: 'gte', label: '≥', input: 'number' },
  { op: 'lt', label: '<', input: 'number' },
  { op: 'lte', label: '≤', input: 'number' },
  { op: 'between', label: 'between', input: 'range' },
]

const DATE_OPS: ColumnFilterOperator[] = [
  { op: 'eq', label: 'on', input: 'date' },
  { op: 'after', label: 'after', input: 'date' },
  { op: 'before', label: 'before', input: 'date' },
  { op: 'gte', label: 'on or after', input: 'date' },
  { op: 'lte', label: 'on or before', input: 'date' },
  { op: 'between', label: 'between', input: 'range' },
]

/* No `on`: see the header. A timestamp equals an instant, not a day. */
const DATETIME_OPS: ColumnFilterOperator[] = [
  { op: 'after', label: 'after', input: 'datetime' },
  { op: 'before', label: 'before', input: 'datetime' },
  { op: 'gte', label: 'on or after', input: 'datetime' },
  { op: 'lte', label: 'on or before', input: 'datetime' },
  { op: 'between', label: 'between', input: 'range' },
]

const BOOLEAN_OPS: ColumnFilterOperator[] = [
  { op: 'eq', label: 'is', input: 'boolean' },
  { op: 'ne', label: 'is not', input: 'boolean' },
]

const OPTION_OPS: ColumnFilterOperator[] = [
  { op: 'in', label: 'is any of', input: 'options' },
  { op: 'eq', label: 'is', input: 'option' },
  { op: 'ne', label: 'is not', input: 'option' },
]

/* A ref is a uuid. `contains` on a uuid is a question nobody means to ask, and
 * the value is picked, never typed — the host brings the Lookup. */
const REF_OPS: ColumnFilterOperator[] = [
  { op: 'eq', label: 'is', input: 'ref' },
  { op: 'ne', label: 'is not', input: 'ref' },
]

/**
 * The conservative set, for a column the metamodel does not describe. Text
 * operators, because an undescribed column is translated as text (that is
 * `leafToPostgrest`'s pre-#959 default and it is unchanged), and NOT the
 * anchored ones — the point of the conservative set is to be small.
 */
const CONSERVATIVE_OPS: ColumnFilterOperator[] = [
  { op: 'contains', label: 'contains', input: 'text' },
  { op: 'eq', label: 'is', input: 'text' },
  { op: 'ne', label: 'is not', input: 'text' },
]

/**
 * The operators for a field, from its KIND. Never a fixed list, and never a
 * list this file decides by looking at the column's NAME — a column called
 * `status` that the dictionary calls text gets text operators, because the
 * dictionary is the answer and a name is a guess.
 */
export function operatorsFor(field: ColumnFilterField): ColumnFilterOperator[] {
  const all = kindOperators(field)
  return field.allowedOps ? all.filter((o) => field.allowedOps!.includes(o.op)) : all
}

function kindOperators(field: ColumnFilterField): ColumnFilterOperator[] {
  switch (field.kind) {
    case 'text': case 'email': case 'phone': case 'country': return TEXT_OPS
    case 'number': case 'money': case 'percent': return NUMBER_OPS
    case 'date': return DATE_OPS
    case 'datetime': return DATETIME_OPS
    case 'boolean': return BOOLEAN_OPS
    case 'ref': return REF_OPS
    // An option column with no vocabulary cannot offer a picker over one. It
    // gets the conservative set and says why, rather than a select with nothing
    // in it.
    case 'option': return field.options?.length ? OPTION_OPS : CONSERVATIVE_OPS
    default: return CONSERVATIVE_OPS
  }
}

/**
 * Why this column got the conservative set — rendered in the menu, so a short
 * operator list is explained rather than merely odd. `null` when it did not.
 */
export function conservativeReason(field: ColumnFilterField): string | null {
  // A host that narrowed the set says why; that sentence outranks the generic
  // ones, because it is the reason the person is actually looking at.
  if (field.allowedOps) return field.restrictionReason ?? 'Only some operators can be applied to this column here.'
  if (field.kind === 'unknown') return 'This column is not described in the field dictionary, so it gets the safe operators.'
  if (field.kind === 'option' && !field.options?.length) return 'No states are registered for this column, so it gets the safe operators.'
  return null
}

/** The editor's working value: one operator and up to two operands. */
export interface ColumnFilterDraft {
  op: string
  value: unknown
  /** the upper bound, for `between` only */
  value2?: unknown
}

/** The default draft for a field — its first operator and no value yet. */
export function emptyDraft(field: ColumnFilterField): ColumnFilterDraft {
  const first = operatorsFor(field)[0]
  // No operator at all (a host that can apply none): an empty draft that no
  // operator makes complete, so Apply can never enable.
  if (!first) return { op: '', value: '' }
  return { op: first.op, value: first.input === 'options' ? [] : '' }
}

/** A draft with no operand is not a filter — Apply stays disabled. */
export function draftIsComplete(draft: ColumnFilterDraft, ops: ColumnFilterOperator[]): boolean {
  const spec = ops.find((o) => o.op === draft.op)
  if (!spec) return false
  const filled = (v: unknown) => (Array.isArray(v) ? v.length > 0 : v !== '' && v != null)
  if (spec.input === 'range') return filled(draft.value) && filled(draft.value2)
  return filled(draft.value)
}

/**
 * Draft → the leaves this column contributes.
 *
 * `between` is the only operator that is not one leaf: the grammar has no
 * `between`, so it emits `gte` + `lte`, which is both faithful and — the part
 * that matters — two operators the translator already pushes to the server.
 */
export function encodeColumnFilter(field: ColumnFilterField, draft: ColumnFilterDraft): ColumnFilterLeaf[] {
  if (draft.op === 'between') {
    return [
      { field: field.key, op: 'gte', value: draft.value },
      { field: field.key, op: 'lte', value: draft.value2 },
    ]
  }
  return [{ field: field.key, op: draft.op, value: draft.value }]
}

/**
 * The leaves on this column → a draft, or `null` when they are not something
 * this editor authored.
 *
 * The `null` case is load-bearing and is NOT a failure: a saved view — or one of
 * the host's curated presets ("Overdue", "Mine") — can legitimately hold a
 * combination no operator/value pair expresses. The editor then shows the column
 * as filtered and offers Clear, instead of silently rewriting somebody's view
 * into the nearest thing it happens to understand.
 */
export function decodeColumnFilter(field: ColumnFilterField, leaves: ColumnFilterLeaf[]): ColumnFilterDraft | null {
  const mine = leaves.filter((l) => l.field === field.key)
  const ops = operatorsFor(field)
  if (mine.length === 1) {
    const [only] = mine as [ColumnFilterLeaf]
    return ops.some((o) => o.op === only.op) ? { op: only.op, value: only.value ?? '' } : null
  }
  if (mine.length === 2 && ops.some((o) => o.op === 'between')) {
    const lo = mine.find((l) => l.op === 'gte')
    const hi = mine.find((l) => l.op === 'lte')
    if (lo && hi) return { op: 'between', value: lo.value ?? '', value2: hi.value ?? '' }
  }
  return null
}

/**
 * What the filter on this column SAYS, in words — for the header's accessible
 * description, the menu's applied-state line and the host's chip.
 *
 * A filtered column that only looks filtered (a tinted glyph) fails both a
 * screen-reader user and anyone who cannot tell the tint from the default. This
 * is the text that stops the row count being unexplained.
 */
export function summarizeColumnFilter(
  field: ColumnFilterField,
  leaves: ColumnFilterLeaf[],
  labelOf?: (value: unknown) => string | undefined,
): string | null {
  const mine = leaves.filter((l) => l.field === field.key)
  if (mine.length === 0) return null
  const show = (v: unknown): string => {
    if (Array.isArray(v)) return v.map(show).join(', ')
    const fromHost = labelOf?.(v)
    if (fromHost) return fromHost
    const opt = field.options?.find((o) => o.value === String(v))
    return opt ? opt.label : String(v ?? '')
  }
  const draft = decodeColumnFilter(field, mine)
  if (draft?.op === 'between') return `${field.label} between ${show(draft.value)} and ${show(draft.value2)}`
  /* Describe EVERY leaf, not only the ones this editor could have authored. A
     registered view holds leaves nobody's menu wrote — a preset, a migration, an
     entity with no `field_meta` at all — and the first draft of this function
     answered those with "Status — 2 conditions". That is strictly less than the
     raw `status in identified,qualifying` it replaced, and the browser suite
     said so: two specs that read the chip went red. A summary that gets vaguer
     the less the metamodel knows is the wrong way round. */
  return mine.map((l) => `${field.label} ${wordFor(field, l.op)} ${show(l.value)}`.trimEnd()).join(' and ')
}

/**
 * One column's filter as a PILL's two halves — `condition` and `value` — in the
 * same words `summarizeColumnFilter` uses (#2375: `Status · is any of ·
 * Service, Subscription`). Null when the column carries no leaf.
 */
export function describeColumnFilter(
  field: ColumnFilterField,
  leaves: ColumnFilterLeaf[],
  labelOf?: (value: unknown) => string | undefined,
): { condition: string; value: string } | null {
  const mine = leaves.filter((l) => l.field === field.key)
  if (mine.length === 0) return null
  const show = (v: unknown): string => {
    if (Array.isArray(v)) return v.map(show).join(', ')
    const fromHost = labelOf?.(v)
    if (fromHost) return fromHost
    const opt = field.options?.find((o) => o.value === String(v))
    return opt ? opt.label : String(v ?? '')
  }
  const draft = decodeColumnFilter(field, mine)
  if (draft?.op === 'between') return { condition: 'between', value: `${show(draft.value)} and ${show(draft.value2)}` }
  if (mine.length === 1) return { condition: wordFor(field, mine[0]!.op), value: show(mine[0]!.value) }
  return { condition: 'matches', value: mine.map((l) => `${wordFor(field, l.op)} ${show(l.value)}`.trimEnd()).join(' and ') }
}

/**
 * The words for an operator: the offered operator's own label where this field
 * offers it — so a date reads "on" and a number reads "=" — and otherwise a
 * generic reading of the leaf grammar, so a leaf this menu would not author is
 * still described rather than counted.
 */
const GENERIC_OPS: Record<string, string> = {
  contains: 'contains', eq: 'is', ne: 'is not', starts: 'starts with', ends: 'ends with',
  in: 'is any of', gt: '>', gte: '≥', lt: '<', lte: '≤',
  after: 'after', before: 'before', empty: 'is empty',
}
function wordFor(field: ColumnFilterField, op: string): string {
  return operatorsFor(field).find((o) => o.op === op)?.label ?? GENERIC_OPS[op] ?? op
}
